<template lang="pug">
	.creatives-list.flex-col.t-padding
		ListView.full-width.creatives-list-view(
			ref="listView",
			initialView="table",
			:remoteSorting="true",
			:hideViewsSelection="true",
			:getTotal="(r) => r.TotalNumberOfRecords",
			:getItems="(r) => prepareData(r)",
			:getData="getData",
			:columns="columns",
			:filterRow="true",
			:columnAutoWidth="false",
			:heightDelta="0",
			:columnHidingEnabled="false",
			:autoFit="true",
			:selectionMode="folderStructureEnabledProp.value ? 'multiple' : null",
			@selection="v => selectedCreatives = v"
		)
			template(slot="left")
				CreativeFolders(v-if="folderStructureEnabledProp.value", ref="folders", @select="onFolderSelected")
			template(v-slot:editing-cell-template="{ data }")
				a.flex-col.creative-name(@click="e => editAudio(data, void 0, e.ctrlKey)", :class="{ 'creative-error': data.GenerationStatus === 4 }", :title="data.Name")
					.flex-row.space.align-center
						fas(v-if="data.GenerationStatus === 4", icon="triangle-exclamation", :title="$tCaption('Error while generating from template')", @click.stop="showCreativeError(data.Oid)")
						fas(v-else-if="[1,2].indexOf(data.GenerationStatus) > -1", icon="spinner" :spin="true", :title="$tCaption('Generating from template')")
						fas(v-else, icon="pen-square")
						.flex-row.wrap {{ $Data.limitText(data.Name, 80) }}
					template(v-if="data.IsTemplate")
						span.grey.font-small ({{$tCaption('Template')}})
					.designed-by(v-if="data.DesignedBy && data.DesignedBy.Name") {{`${$tCaption('Designed by: ')} ${data.DesignedBy.Name}`}}
			template(v-slot:actions-cell-template="{ data }")
				DxDropDownButton(v-if="getActions(data).length", icon="more", :items="getActions(data)", stylingMode="text",  display-expr="name", key-expr="id", :use-select-mode="false", :showArrowIcon="false", :drop-down-options="{ width: 230 }", @item-click="e => onActionClick(e, data)")
			template(slot="left-actions")
				.flex-row.space.align-center
					.font-large {{$tCaption('Creatives')}}
					.flex-col
						DxButton(:icon="isAddingCreative ? 'fas fa-spinner fa-spin' : 'plus'", ref="newCreativeButton", @click="onNewCreativeButtonClick", :hint="$tCaption('New creative')", :disabled="isAddingCreative")
						.new-creative-context-menu
						DxContextMenu(v-if="$auth.hasCreativeApi()", ref="creativeContextMenu", :data-source="newCreativeMenu", target=".new-creative-context-menu", :width="200", @item-click="onNewCreativeMenuItemClick")
					DxButton.medium(icon="fas fa-scroll", :hint="$tCaption('Go to New Script Builder')" @click="() => $router.push({ path:'/creative/ScriptBuilder'})")
					DxButton.medium(icon="fas fa-th", @click="openTemplates", :hint="$tCaption('Templates')")
					DxButton.medium(v-if="selectedCreatives.length", icon="fas fa-folder-tree", @click="changeFolder", :hint="$tCaption('Change folder')")
			template(slot="right-actions")
				.flex-row.space-large.align-center
					.flex-row.space
						.upper.grey {{ $tCaption('Show Templates') }}
						DxSwitch(v-model="showTemplates")
					.flex-row
						.adt-tab.flex-row.space.align-center(v-for="view in views", :title="view.hint", :class="{ active: (view.id === 'folders') === folderStructureEnabledProp.value }"
							@click="e => folderStructureEnabledProp.value = (view.id === 'folders')"
						)
							fas(:icon="view.icon", size="xs")
			.add-button
				DxButton(icon="plus", type="default", @click="addCreative")
		CreativeVastLink(ref="vastLink")
		CreativePublish(
			ref="creativePublish",
			@publish="onPublishFinalAudioServerside",
			@publish-initialized="onPublishFinalAudioServersideInitialized"
		)
		CreativeTemplates(ref="creativeTemplates", :folderId="selectedFolderId")
		Loader(v-if="isLoadingError", :fullscreen="true", text="")
		CompanyInfoChecker(ref="companyInfoChecker")
</template>

<script>
	import _ from 'lodash';
	import Data from 'app-core/Data';
	import { DxDataGrid, DxPaging, DxColumn, DxSelection, DxSummary, DxTotalItem } from 'devextreme-vue/data-grid';
	import Pager from 'app-core/Pager.vue';
	import DxButton from 'devextreme-vue/button';
	import { DxSwitch } from 'devextreme-vue/switch';
	import ListView from 'app-core/ListView.vue';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import CreativeVastLink from './CreativeVastLink.vue';
	import CreativePublish from './CreativePublish.vue';
	import CreativeTemplates from './CreativeTemplates.vue';
	import Loader from 'app-core/Loader.vue';
	import CompanyInfoChecker from 'view/company/CompanyInfoChecker.vue';
	import DxTreeView from 'devextreme-vue/tree-view';
	import CreativeFolders from './CreativeFolders.vue';
	import storage from 'storage';
	import DxContextMenu from 'devextreme-vue/context-menu';

	export default {
		translationCategory: 'creative.List',
		name: 'creatives-list',
		components: {
			DxDataGrid,
			DxPaging,
			DxColumn,
			DxSelection,
			DxSummary,
			DxTotalItem,
			Pager,
			DxButton,
			DxSwitch,
			ListView,
			DxDropDownButton,
			CreativeVastLink,
			CreativePublish,
			CreativeTemplates,
			Loader,
			CompanyInfoChecker,
			DxTreeView,
			CreativeFolders,
			DxContextMenu
		},
		data() {
			let simplifiedColumns = [];
			if (!this.$auth.isAgency())
				simplifiedColumns.push({ dataField: 'AgencyName', caption: 'Agency' });
			if (!this.$auth.isAdvertiser())
				simplifiedColumns.push({ dataField: 'AdvertiserName', caption: 'Advertiser' });
			return {
				currentPage: 0,
				creatives: {},
				bottomPlayer: {
					isInitiated: false,
					playState: false,
					audioLink: null,
					fileName: null,
					title: null,
					duration: null,
					fieldName: null
				},
				columns: [
					{ dataField: 'Name', caption: () => this.$tCaption('Name'), cellTemplate: 'editing-cell-template', width: 310 },
					{ dataField: 'Isci', caption: () => this.$tCaption('ISCI'), width: 110 },
					{ dataField: 'DisplayLength', caption: () => this.$tCaption('Length (sec)'), dataType: 'number', width: 110 },
					...(this.$auth.hasCreativeApi() ? [
						{ dataField: 'IsPublished', caption: () => this.$tCaption('Published'), dataType: 'boolean', width: 110 },
					] : [
						{ dataField: 'Trimmed', caption: () => this.$tCaption('Trimmed'), dataType: 'boolean', width: 110 },
						{ dataField: 'Normalized', caption: () => this.$tCaption('Normalized'), dataType: 'boolean', width: 110 },
					]),
					...simplifiedColumns,
					{ dataField: 'Name', name: 'Actions', cellTemplate: 'actions-cell-template', caption: '', width: 80 }
				],
				actions: [
					{ id: 'publish', name: this.$tCaption('Publish'), icon: 'fas fa-check', hidden: (c) => c.IsPublished || c.IsPublishing },
					{ id: 'vast', name: this.$tCaption('Get VAST Tag'), icon: 'fas fa-copy', hidden: (c) => !c.IsPublished },
					{ id: 'copy', name: this.$tCaption('Make a copy'), icon: 'fas fa-clone' },
					{ id: 'delete', name: this.$tCaption('Delete'), icon: 'fas fa-times', hidden: (c) => c.IsPublished },
				],
				listItems: [],
				showTemplates: false,
				isLoadingError: false,
				isAddingCreative: false,
				selectedFolderId: storage.prop('creative-folders-selected', void 0, null).value,
				folderStructureEnabledProp: storage.prop('creative-folders-enabled', void 0, true),
				selectedCreatives: [],
				views: [
					{ icon: 'folder-tree', hint: this.$tCaption('Folders structure'), id: 'folders' },
					{ icon: 'list', hint: this.$tCaption('Plain table'), id: 'plain' }
				],
				newCreativeMenu: [
					{ text: this.$tCaption('New creative file'), action: 'creativeFile', icon: 'fas fa-file-audio' },
					{ text: this.$tCaption('New creative studio project'), action: 'creativeStudio', icon: 'fas fa-square-poll-horizontal' },
					{ text: this.$tCaption('SonicScribe project'), action: 'sonicScribe', icon: 'fas fa-indent' },
				]
			};
		},
		computed: {
			grid() {
				return this.$refs.grid && this.$refs.grid.instance;
			}
		},
		watch: {
			showTemplates() {
				this.reloadListView();
			},
			'folderStructureEnabledProp.value'() {
				this.reloadListView();
			}
		},
		created() {
			if (__DEBUG__)
				window.creativesList = this;
			this.$eventsBus.$on('signalr-on-notification', this.signalrOnNotification);
			this.$reloadListview = _.debounce(this.reloadListView, 300, {
				'leading': false,
				'trailing': true,
			});
		},
		beforeDestroy() {
			this.$eventsBus.$off('signalr-on-notification', this.signalrOnNotification);
		},
		methods: {
			onNewCreativeButtonClick() {
				this.$nextTick(() => {
					if (this.$auth.hasCreativeApi())
						this.$refs.creativeContextMenu.instance.show()
					else
						this.addCreative();
				});
			},
			onNewCreativeMenuItemClick(e) {
				const action = e.itemData.action;
				switch(action) {
					case 'creativeFile':
						this.addCreative({
							data: {
								JsonConfig: {
									isCreativeFile: true
								}
							}
						});
						break;
					case 'creativeStudio':
						this.addCreative();
						break;
					case 'sonicScribe':
						this.addCreative({
							data: {
								JsonConfig: {
									isSonicScribe: true
								},
								Name: this.$tCaption('Sonic scribe: new project')
							}
						});
						break;
				}
			},
			changeFolder() {
				if (this.$refs.folders?.selectFolder) {
					this.$refs.folders.selectFolder(this.selectedFolderId, (newFolderId) => {
						if (__DEBUG__)
							console.debug(`Change folder ${this.selectedFolderId} -> ${newFolderId}`);
						
						this.selectedCreatives.map(f => f.GenerationStatus = 1);
						this.$Data.postExternalApi('Service/CreateUpdateObjects', this.selectedCreatives.map(f => ({
							Id: f.Oid,
							Type: this.$Data.objectTypes.Creative,
							Fields: [
								{
									Name: 'Folder',
									Value: newFolderId
								}
							]
						})))
						.then(r => {
							const error = r.Data?.find(f => f.Success === false)?.Error;
							if (error) {
								this.$notifyError(error);
							} else {
								this.deselect();
							}
						})
						.finally(() => {
							this.reloadListView();
						})
					});
				}
			},
			deselect() {
				if (this.$refs.listView?.$refs.table?.instance)
					this.$refs.listView?.$refs.table?.instance?.clearSelection();
			},
			reloadListView(preventDeselect) {
				if (!preventDeselect)
					this.deselect();
				if (this.$refs.listView)
					this.$refs.listView.reload();
			},
			onFolderSelected(folder) {
				if (this.selectedFolderId !== folder?.Oid) {
					this.deselect();
					this.selectedFolderId = folder?.Oid;
					this.$reloadListview();
				}
			},
			showCreativeError(id) {
				this.isLoadingError++;
				this.$Data.getExternalApi('Creative/GetCreative', `creativeId=${id}`).then(r => {
					this.$notifyMessage(r?.Logs || ' ', { fullScreen: true });
				}).finally(() => this.isLoadingError--);
			},
			getActions(creative) {
				return this.actions.filter(f => !f.hidden || !f.hidden(creative));
			},
			getData(pageIndex, limit, opts) {
				this.isLoading++;
				let options = { ...opts };
				const templatesFilter = opts?.filter
					? [opts.filter , 'and', ['IsTemplate', 'contains', this.showTemplates]]
					: ['IsTemplate', 'contains', this.showTemplates];
				const foldersFilter = ['Folder.Oid', '=', this.selectedFolderId || null];
				options.filter = this.folderStructureEnabledProp.value ? [foldersFilter, 'and', templatesFilter] : templatesFilter;
				return this.$Data
					.getAllCreatives(pageIndex, limit, options)
					.then((r) => r)
					.finally(() => this.isLoading--);
			},
			prepareData(r) {
				this.listItems = (r?.Results || [])
					.map(f => ({
						IsPublishing: false,
						...f,
						Length: parseInt(f.Length || parseFloat(f.Duration / 1000).toFixed(1) || 0)
					}));
				return this.listItems;
			},
			async addCreative(opts) {
				try {
					this.isAddingCreative = true;
					await this.$refs.companyInfoChecker.checkTimeZones();
					return Data
						.createUpdateCreative({
							Oid: null,
							Name: 'New creative',
							Folder: {
								Oid: this.selectedFolderId || null
							},
							...(opts?.data || {})
						})
						.then(r => {
							if (__DEBUG__)
								console.log('Create spot result', { r });
							if (r.Success)
								this.editAudio(r.Data, true);
							else
								this.$notifyError(r.ErrorMessage);
						})
						.finally(() => this.isAddingCreative = false);
				} catch {
					this.isAddingCreative = false
				}
			},
			editAudio(record, isNew, separateWindow) {
				let lastRevisionId = (this.$Data.getRevisionsFromString(record?.RootLastRevisionIds) || []).filter(f => f?.languageCode === record?.LanguageCode)[0]?.creativeId || record.Oid;
				let route = { path: `/creative/Detail/${lastRevisionId}`, params: { isNew } };
				if (separateWindow) {
					let routeData = this.$router.resolve(route);
					window.open(routeData.href, '_blank');
				} else {
					this.$router.push(route);
				}
			},
			onActionClick(e, creative) {
				switch(e.itemData.id) {
					case 'vast':
						this.$refs.vastLink.open(creative.Oid);
						break;
					case 'copy':
						this.$Data
							.cloneCreative(creative.Oid, creative.Name, true)
							.then(() => this.reloadListView());
						break;
					case 'publish':
						this.$refs.creativePublish.publish(creative);
						break;
					case 'delete':
						this.$confirmMessage({
							title: this.$tCaption('Confirm action', '@actions'),
							text: this.$tCaption('Are you sure you want to delete the current creative?', '@creative.Detail'),
							handler: () => {
								Data.getExternalApi('Creative/DeleteCreative', `creativeId=${creative.Oid}`).then(() => {
									this.$notifySuccess(this.$tCaption('Creative deleted'));
									this.reloadListView();
								});
							}
						});
						break;
				}
			},
			onPublishFinalAudioServerside(retryPublicationSessionId, file, creative) {
				creative.IsPublishing = true;
			},
			onPublishFinalAudioServersideInitialized() {
			},
			openTemplates() {
				if (this.$refs.creativeTemplates)
					this.$refs.creativeTemplates.showPopup();
			},
			signalrOnNotification({eventName, data}) {
				const publishEvent = eventName === 'CreativePublished';
				const generationEvent = eventName === 'CreativeGenerationStatusChanged';
				if ((publishEvent || generationEvent) && data?.Id) {
					const itemExists = this.listItems.find(item => item.Oid === data.Id);
					if (publishEvent && itemExists || generationEvent && !itemExists) {
						this.reloadListView();
					} else if (generationEvent && itemExists) {
						itemExists.GenerationStatus = data.GenerationStatus;
					}
				}
			},
		}
	};
</script>

<style lang='scss'>
	.creatives-list {
		.creatives-list-view .view-holder {
			border-bottom: 1px solid #dedede;
			border-top: 1px solid #dedede;
		}

		.creative-error {
			color: #ffbfbf;
		}

		.creative-name {
			
			.designed-by {
				font-size: 10px;
			}
		}
	}
</style>
